import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { PanelCard } from './PanelCard';
import { RingGauge } from './Ring';
import { usePanelTokens } from './tokens';

export interface RingKpiCardProps {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  /** Ring fill 0–100 (clamped). */
  ring: number;
  /** Text in the ring centre (e.g. "87%"). */
  ringText?: ReactNode;
  /** Optional second ring segment up to this value (e.g. committed after spent). */
  ringSecondary?: number;
  /** Optional hand on the ring (e.g. year elapsed). */
  hand?: number;
  /** Tone of ring and value: neutral (brand), good, warning, over. */
  tone?: 'neutral' | 'good' | 'warning' | 'over' | 'muted';
  ariaLabel?: string;
}

/** The ring KPI tile used across the budget screens (ring left, label/value/sub right). */
export function RingKpiCard({
  label, value, sub, ring, ringText, ringSecondary, hand, tone = 'neutral', ariaLabel,
}: RingKpiCardProps) {
  const t = usePanelTokens();
  const color = {
    neutral: t.a, good: t.positive, warning: t.amber, over: t.negative, muted: t.label,
  }[tone];
  const valueColor = tone === 'neutral' || tone === 'muted' ? t.ink : color;
  return (
    <PanelCard sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <RingGauge
          value={ring}
          secondaryValue={ringSecondary}
          hand={hand}
          size={68}
          stroke={8}
          color={color}
          centerTop={<Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 800 }}>{ringText}</Box>}
          ariaLabel={ariaLabel}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: t.label, fontWeight: 600, lineHeight: 1.3 }}>
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: '1.15rem', fontWeight: 800, color: valueColor, lineHeight: 1.2,
              fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere',
            }}
          >
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: '0.72rem', color: t.label, mt: 0.25, lineHeight: 1.35 }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Box>
    </PanelCard>
  );
}
